import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Detect language from URL path
const getLanguageFromPath = (pathname) => {
  return pathname.startsWith('/fr') ? 'fr' : 'en';
};

// Remove language prefix from path
const getPathWithoutLang = (pathname) => {
  if (pathname.startsWith('/fr')) {
    return pathname.slice(3) || '/';
  }
  return pathname;
};

// Add language prefix to path
const getLocalizedPath = (pathname, lang) => {
  const cleanPath = getPathWithoutLang(pathname);
  if (lang === 'fr') {
    return `/fr${cleanPath === '/' ? '' : cleanPath}`;
  }
  return cleanPath;
};

// Detect browser language preference
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  // Check if browser language starts with 'fr' (covers fr, fr-CA, fr-FR, etc.)
  return browserLang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
};

// Check if user has visited before (has a language preference saved)
const hasVisitedBefore = () => {
  return localStorage.getItem('kenscho_lang_detected') === 'true';
};

// Mark that we've done the initial language detection
const markAsVisited = () => {
  localStorage.setItem('kenscho_lang_detected', 'true');
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasRedirected, setHasRedirected] = useState(false);
  
  // Determine language from URL
  const [language, setLanguageState] = useState(() => {
    return getLanguageFromPath(location.pathname);
  });

  // Auto-detect and redirect on first visit to ANY English page
  useEffect(() => {
    // Only redirect if:
    // 1. User hasn't been redirected in this session
    // 2. User hasn't visited before (no saved preference)
    // 3. User is on an English page (not already on /fr/...)
    const isEnglishPage = !location.pathname.startsWith('/fr');
    
    if (!hasRedirected && !hasVisitedBefore() && isEnglishPage) {
      const browserLang = detectBrowserLanguage();
      markAsVisited();
      setHasRedirected(true);
      
      if (browserLang === 'fr') {
        // Redirect French speakers to French version of current page
        const frenchPath = getLocalizedPath(location.pathname, 'fr');
        navigate(frenchPath, { replace: true });
      }
    }
  }, [location.pathname, hasRedirected, navigate]);

  // Update language when URL changes
  useEffect(() => {
    const urlLang = getLanguageFromPath(location.pathname);
    if (urlLang !== language) {
      setLanguageState(urlLang);
    }
    document.documentElement.lang = urlLang;
  }, [location.pathname, language]);

  // Toggle language and navigate to the equivalent page in the other language
  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'fr' : 'en';
    const newPath = getLocalizedPath(location.pathname, newLang);
    navigate(newPath);
  };

  // Set language and navigate
  const setLanguage = (newLang) => {
    if (newLang !== language) {
      const newPath = getLocalizedPath(location.pathname, newLang);
      navigate(newPath);
    }
  };

  // Get localized link path
  const getLocalizedLink = (path) => {
    return getLocalizedPath(path, language);
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      toggleLanguage, 
      t, 
      getLocalizedLink,
      getPathWithoutLang,
      getLocalizedPath 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      portfolio: 'Portfolio',
      contact: 'Contact',
      divisions: 'Divisions',
      equipmentRental: 'Equipment Rental',
      construction: 'Construction',
      workSite: 'Work-Site',
      transport: 'Transport',
      concrete: 'Concrete',
      excavation: 'Excavation',
      welding: 'Welding',
      getQuote: 'Get a Quote'
    },
    home: {
      tagline: 'Construction • Renovation • Equipment Rental',
      heroTitle1: 'Building Dreams,',
      heroTitle2: 'Crafting Spaces',
      heroDesc: 'Kenscho Design delivers quality construction services for residential, commercial, and industrial projects across Montreal and surrounding regions.',
      getQuote: 'Get a Free Quote',
      viewWork: 'View Our Work',
      serving: 'Serving:',
      whatWeDo: 'What We Do',
      ourServices: 'Our Services',
      viewAllServices: 'View All Services',
      whyKenscho: 'Why Kenscho Design',
      qualityWork: 'Quality Work,',
      trustedResults: 'Trusted Results',
      whyPoints: [
        'Licensed general contractor with years of experience',
        'Specializing in kitchen and bathroom renovations',
        'Residential, commercial, and industrial projects',
        'Property maintenance and handyman services',
        'Serving Lanaudière, Laurentians, Laval & Montreal'
      ],
      contactToday: 'Contact Us Today',
      yearsExp: 'Years of combined experience in construction',
      ourDivisions: 'Our Divisions',
      moreThanConstruction: 'More Than Construction',
      divisionEquipment: 'Professional tools and equipment for contractors and DIY enthusiasts.',
      divisionWorksite: 'Heavy machinery and work-site equipment for your construction projects.',
      divisionTransport: 'Reliable transport solutions for equipment, materials, and debris.',
      learnMore: 'Learn More',
      readyToStart: 'Ready to Start Your Project?',
      readyDesc: 'Whether it\'s a kitchen renovation, commercial buildout, or property maintenance, we\'re here to bring your vision to life.',
      getFreeQuote: 'Get Your Free Quote'
    },
    services: {
      tagline: 'Our Services',
      title1: 'Construction &',
      title2: 'Renovation Experts',
      desc: 'From kitchen renovations to industrial buildouts, we deliver quality craftsmanship across residential, commercial, and industrial sectors.',
      serviceAreas: 'Service Areas',
      whereWeWork: 'Where We Work',
      areaDesc: 'We proudly serve the greater Montreal area and surrounding regions, bringing quality construction services to your doorstep.',
      allNeighborhoods: 'All neighborhoods',
      completeCoverage: 'Complete coverage',
      fullRegion: 'Full region',
      southRegion: 'South region',
      projectInMind: 'Have a Project in Mind?',
      contactForQuote: 'Contact us for a free consultation and quote. We\'ll work with you to bring your vision to life.',
      getFreeQuote: 'Get Your Free Quote'
    },
    portfolio: {
      tagline: 'Our Work',
      title1: 'Project',
      title2: 'Portfolio',
      desc: 'Browse through our completed projects to see the quality and craftsmanship we bring to every job.',
      allProjects: 'All Projects',
      noProjects: 'No projects found in this category.',
      readyToStart: 'Ready to Start Your Project?',
      letUsHelp: 'Let us help bring your vision to life. Contact us today for a free consultation.',
      getFreeQuote: 'Get a Free Quote'
    },
    contact: {
      tagline: 'Get in Touch',
      title1: 'Contact',
      title2: 'Us',
      desc: 'Have a project in mind? We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.',
      discussProject: 'Let\'s Discuss Your Project',
      discussDesc: 'Whether you need a kitchen renovation, commercial buildout, or equipment rental, we\'re here to help. Reach out to us and let\'s bring your vision to life.',
      serviceAreas: 'Service Areas',
      email: 'Email',
      phone: 'Phone',
      businessHours: 'Business Hours',
      hoursText: 'Monday - Friday: 8:00 AM - 6:00 PM\nSaturday: 9:00 AM - 4:00 PM',
      sendMessage: 'Send Us a Message',
      fullName: 'Full Name',
      emailAddress: 'Email Address',
      phoneNumber: 'Phone Number',
      serviceInterested: 'Service Interested In',
      selectService: 'Select a service',
      yourMessage: 'Your Message',
      messagePlaceholder: 'Tell us about your project...',
      submit: 'Send Message',
      sending: 'Sending...',
      thankYou: 'Thank You!',
      receivedMessage: 'We\'ve received your message and will get back to you within 24 hours.',
      sendAnother: 'Send Another Message',
      successToast: 'Message sent successfully! We\'ll get back to you soon.',
      errorToast: 'Failed to send message. Please try again.',
      serviceOptions: {
        construction: 'Construction',
        equipment: 'Equipment Rental',
        worksite: 'Work-Site',
        transport: 'Transport',
        concrete: 'Concrete',
        excavation: 'Excavation',
        welding: 'Welding',
        other: 'Other'
      }
    },
    equipment: {
      tagline: 'Kenscho Equipment Rental',
      title1: 'Equipment Kenscho',
      title2: 'Rental',
      desc: 'Kenscho Equipment Rental offers quality tools and equipment for contractors, tradespeople, and DIY enthusiasts. Competitive rates with flexible rental periods from Equipment Kenscho Rental.',
      available: 'Available Equipment',
      browseSelection: 'Browse our selection of professional-grade tools from Kenscho Equipment Rental',
      customRequest: 'Custom Request',
      daily: 'Daily',
      weekly: 'Weekly',
      inquire: 'Inquire',
      availableBadge: 'Available',
      rentedBadge: 'Rented',
      rentalInfo: 'Kenscho Equipment Rental Information',
      rentalPoints: [
        { title: 'Flexible Periods', desc: 'Daily, weekly, and monthly rental options available at Equipment Kenscho Rental' },
        { title: 'Delivery Available', desc: 'Kenscho Equipment Rental can deliver equipment directly to your job site' },
        { title: 'Professional Grade', desc: 'All tools at Equipment Kenscho Rental are maintained to professional standards' },
        { title: 'Training Available', desc: 'Equipment orientation provided upon request by Kenscho Equipment Rental' }
      ],
      howToRent: 'How to Rent from Equipment Kenscho Rental',
      steps: [
        'Browse our available equipment at Kenscho Equipment Rental',
        'Contact us with your rental needs',
        'Pick up or have it delivered',
        'Return when finished'
      ],
      inquireAbout: 'Inquire About Rental'
    },
    worksite: {
      tagline: 'Kenscho Work-Site',
      title1: 'Heavy',
      title2: 'Equipment Rental',
      desc: 'Professional work-site equipment for construction projects of all sizes. Excavators, loaders, lifts, and more.',
      excavation: 'Excavation',
      professionalMachinery: 'Professional machinery for your construction needs',
      requestEquipment: 'Request Equipment',
      whyChoose: 'Why Choose Kenscho Work-Site',
      whyDesc: 'We provide reliable, well-maintained equipment to keep your project on schedule.',
      features: [
        { title: 'Well-Maintained Fleet', desc: 'All equipment is regularly serviced and inspected for safety and reliability.' },
        { title: 'Operator Available', desc: 'Certified operators available for hire with equipment if needed.' },
        { title: 'Flexible Terms', desc: 'Daily, weekly, or long-term rentals to fit your project timeline.' }
      ],
      getQuote: 'Get a Quote'
    },
    transport: {
      tagline: 'Kenscho Transport',
      title1: 'Transport',
      title2: 'Solutions',
      desc: 'Reliable transport services for equipment, materials, and debris removal. Serving Montreal and surrounding areas.',
      fleet: 'Transport Fleet',
      fleetDesc: 'Trucks and trailers for all your hauling needs',
      scheduleTransport: 'Schedule Transport',
      services: 'Transport Services',
      serviceItems: [
        { title: 'Equipment Transport', desc: 'Safe and secure transport of heavy machinery and construction equipment.' },
        { title: 'Material Delivery', desc: 'Timely delivery of construction materials to your job site.' },
        { title: 'Debris Removal', desc: 'Efficient removal and disposal of construction debris and waste.' },
        { title: 'Specialized Hauling', desc: 'Custom transport solutions for unique or oversized loads.' }
      ],
      serviceAreasTitle: 'Service Areas',
      serviceAreasDesc: 'We provide transport services throughout Lanaudière, Laurentians, Laval, Montreal and surrounding regions.',
      areas: ['Lanaudière', 'Laurentians', 'Laval', 'Montreal', 'South Shore'],
      requestTransport: 'Request Transport'
    },
    footer: {
      desc: 'Quality construction services for residential, commercial, and industrial projects across Lanaudière, Laurentians, Laval and Montreal.',
      quickLinks: 'Quick Links',
      ourDivisions: 'Our Divisions',
      contactUs: 'Contact Us',
      rights: 'All rights reserved.'
    },
    servicesList: {
      kitchen: {
        title: 'Kitchen Renovations',
        desc: 'Transform your kitchen into a modern, functional space. From cabinet installation to complete remodels.',
        features: ['Custom cabinetry', 'Countertop installation', 'Plumbing upgrades', 'Electrical work', 'Flooring']
      },
      bathroom: {
        title: 'Bathroom Renovations',
        desc: 'Create your perfect bathroom sanctuary with our expert renovation services.',
        features: ['Tile installation', 'Fixture replacement', 'Vanity installation', 'Shower/tub upgrades', 'Accessibility modifications']
      },
      homeExtensions: {
        title: 'Home Extensions & Detached Garages',
        desc: 'Expand your living space with custom home additions and standalone garage construction.',
        features: ['Room additions', 'Detached garages', 'In-law suites', 'Sunrooms', 'Second story additions']
      },
      industrial: {
        title: 'Industrial Construction',
        desc: 'Heavy-duty industrial construction and facility improvements.',
        features: ['Facility upgrades', 'Equipment foundations', 'Safety improvements', 'Loading docks', 'Industrial flooring']
      },
      commercial: {
        title: 'Commercial Construction',
        desc: 'Professional commercial construction services for businesses of all sizes.',
        features: ['Tenant improvements', 'Office buildouts', 'Retail spaces', 'Restaurant construction', 'Warehouse modifications']
      },
      maintenance: {
        title: 'Property Maintenance',
        desc: 'Reliable maintenance services to keep your property in top condition.',
        features: ['Repairs & fixes', 'Seasonal maintenance', 'Preventive care', 'Emergency services', 'Handyman tasks']
      }
    }
  },
  fr: {
    nav: {
      home: 'Accueil',
      services: 'Services',
      portfolio: 'Portfolio',
      contact: 'Contact',
      divisions: 'Divisions',
      equipmentRental: 'Location d\'équipement',
      construction: 'Construction',
      workSite: 'Chantier',
      transport: 'Transport',
      concrete: 'Béton',
      excavation: 'Excavation',
      welding: 'Soudure',
      getQuote: 'Obtenir un devis'
    },
    home: {
      tagline: 'Construction • Rénovation • Location d\'équipement',
      heroTitle1: 'Bâtir des rêves,',
      heroTitle2: 'Créer des espaces',
      heroDesc: 'Kenscho Design offre des services de construction de qualité pour les projets résidentiels, commerciaux et industriels à travers Montréal et les régions environnantes.',
      getQuote: 'Obtenir un devis gratuit',
      viewWork: 'Voir nos réalisations',
      serving: 'Desservant :',
      whatWeDo: 'Ce que nous faisons',
      ourServices: 'Nos services',
      viewAllServices: 'Voir tous les services',
      whyKenscho: 'Pourquoi Kenscho Design',
      qualityWork: 'Travail de qualité,',
      trustedResults: 'Résultats fiables',
      whyPoints: [
        'Entrepreneur général licencié avec des années d\'expérience',
        'Spécialisé dans les rénovations de cuisines et salles de bains',
        'Projets résidentiels, commerciaux et industriels',
        'Entretien de propriété et services de bricolage',
        'Desservant Lanaudière, Laurentides, Laval et Montréal'
      ],
      contactToday: 'Contactez-nous aujourd\'hui',
      yearsExp: 'Années d\'expérience combinée en construction',
      ourDivisions: 'Nos divisions',
      moreThanConstruction: 'Plus que de la construction',
      divisionEquipment: 'Outils et équipements professionnels pour entrepreneurs et bricoleurs.',
      divisionWorksite: 'Machinerie lourde et équipement de chantier pour vos projets de construction.',
      divisionTransport: 'Solutions de transport fiables pour équipements, matériaux et débris.',
      learnMore: 'En savoir plus',
      readyToStart: 'Prêt à démarrer votre projet ?',
      readyDesc: 'Qu\'il s\'agisse d\'une rénovation de cuisine, d\'un aménagement commercial ou d\'un entretien de propriété, nous sommes là pour concrétiser votre vision.',
      getFreeQuote: 'Obtenir votre devis gratuit'
    },
    services: {
      tagline: 'Nos services',
      title1: 'Experts en',
      title2: 'Construction et rénovation',
      desc: 'Des rénovations de cuisine aux aménagements industriels, nous offrons un travail de qualité dans les secteurs résidentiel, commercial et industriel.',
      serviceAreas: 'Zones de service',
      whereWeWork: 'Où nous travaillons',
      areaDesc: 'Nous desservons fièrement la grande région de Montréal et les environs, apportant des services de construction de qualité à votre porte.',
      allNeighborhoods: 'Tous les quartiers',
      completeCoverage: 'Couverture complète',
      fullRegion: 'Région complète',
      southRegion: 'Région sud',
      projectInMind: 'Vous avez un projet en tête ?',
      contactForQuote: 'Contactez-nous pour une consultation et un devis gratuits. Nous travaillerons avec vous pour concrétiser votre vision.',
      getFreeQuote: 'Obtenir votre devis gratuit'
    },
    portfolio: {
      tagline: 'Nos réalisations',
      title1: 'Portfolio de',
      title2: 'projets',
      desc: 'Parcourez nos projets complétés pour voir la qualité et le savoir-faire que nous apportons à chaque travail.',
      allProjects: 'Tous les projets',
      noProjects: 'Aucun projet trouvé dans cette catégorie.',
      readyToStart: 'Prêt à démarrer votre projet ?',
      letUsHelp: 'Laissez-nous vous aider à concrétiser votre vision. Contactez-nous aujourd\'hui pour une consultation gratuite.',
      getFreeQuote: 'Obtenir un devis gratuit'
    },
    contact: {
      tagline: 'Nous joindre',
      title1: 'Contactez',
      title2: '-nous',
      desc: 'Vous avez un projet en tête ? Nous serions ravis de vous entendre. Envoyez-nous un message et nous vous répondrons dans les plus brefs délais.',
      discussProject: 'Discutons de votre projet',
      discussDesc: 'Que vous ayez besoin d\'une rénovation de cuisine, d\'un aménagement commercial ou d\'une location d\'équipement, nous sommes là pour vous aider. Contactez-nous et concrétisons votre vision.',
      serviceAreas: 'Zones de service',
      email: 'Courriel',
      phone: 'Téléphone',
      businessHours: 'Heures d\'ouverture',
      hoursText: 'Lundi - Vendredi : 8h00 - 18h00\nSamedi : 9h00 - 16h00',
      sendMessage: 'Envoyez-nous un message',
      fullName: 'Nom complet',
      emailAddress: 'Adresse courriel',
      phoneNumber: 'Numéro de téléphone',
      serviceInterested: 'Service qui vous intéresse',
      selectService: 'Sélectionnez un service',
      yourMessage: 'Votre message',
      messagePlaceholder: 'Parlez-nous de votre projet...',
      submit: 'Envoyer le message',
      sending: 'Envoi en cours...',
      thankYou: 'Merci !',
      receivedMessage: 'Nous avons reçu votre message et vous répondrons dans les 24 heures.',
      sendAnother: 'Envoyer un autre message',
      successToast: 'Message envoyé avec succès ! Nous vous répondrons bientôt.',
      errorToast: 'Échec de l\'envoi du message. Veuillez réessayer.',
      serviceOptions: {
        construction: 'Construction',
        equipment: 'Location d\'équipement',
        worksite: 'Chantier',
        transport: 'Transport',
        concrete: 'Béton',
        excavation: 'Excavation',
        welding: 'Soudure',
        other: 'Autre'
      }
    },
    equipment: {
      tagline: 'Location Kenscho',
      title1: 'Location Kenscho',
      title2: 'd\'équipement',
      desc: 'Location Kenscho offre des outils et équipements de qualité pour entrepreneurs, gens de métier et bricoleurs. Tarifs compétitifs avec périodes de location flexibles chez Location Kenscho.',
      available: 'Équipement disponible',
      browseSelection: 'Parcourez notre sélection d\'outils professionnels chez Location Kenscho',
      customRequest: 'Demande spéciale',
      daily: 'Journalier',
      weekly: 'Hebdomadaire',
      inquire: 'S\'informer',
      availableBadge: 'Disponible',
      rentedBadge: 'Loué',
      rentalInfo: 'Informations Location Kenscho',
      rentalPoints: [
        { title: 'Périodes flexibles', desc: 'Options de location quotidienne, hebdomadaire et mensuelle disponibles chez Location Kenscho' },
        { title: 'Livraison disponible', desc: 'Location Kenscho peut livrer l\'équipement directement sur votre chantier' },
        { title: 'Qualité professionnelle', desc: 'Tous les outils chez Location Kenscho sont entretenus selon les normes professionnelles' },
        { title: 'Formation disponible', desc: 'Orientation sur l\'équipement fournie sur demande par Location Kenscho' }
      ],
      howToRent: 'Comment louer chez Location Kenscho',
      steps: [
        'Parcourez l\'équipement disponible chez Location Kenscho',
        'Contactez-nous avec vos besoins de location',
        'Récupérez ou faites-vous livrer',
        'Retournez une fois terminé'
      ],
      inquireAbout: 'S\'informer sur la location'
    },
    worksite: {
      tagline: 'Kenscho Chantier',
      title1: 'Location d\'équipement',
      title2: 'lourd',
      desc: 'Équipement de chantier professionnel pour projets de construction de toutes tailles. Excavateurs, chargeuses, nacelles et plus encore.',
      heavyEquipment: 'Équipement lourd',
      professionalMachinery: 'Machinerie professionnelle pour vos besoins de construction',
      requestEquipment: 'Demander de l\'équipement',
      whyChoose: 'Pourquoi choisir Kenscho Chantier',
      whyDesc: 'Nous fournissons de l\'équipement fiable et bien entretenu pour garder votre projet sur les rails.',
      features: [
        { title: 'Flotte bien entretenue', desc: 'Tout l\'équipement est régulièrement entretenu et inspecté pour la sécurité et la fiabilité.' },
        { title: 'Opérateur disponible', desc: 'Opérateurs certifiés disponibles à l\'embauche avec l\'équipement si nécessaire.' },
        { title: 'Conditions flexibles', desc: 'Locations quotidiennes, hebdomadaires ou à long terme selon votre calendrier de projet.' }
      ],
      getQuote: 'Obtenir un devis'
    },
    transport: {
      tagline: 'Kenscho Transport',
      title1: 'Solutions de',
      title2: 'transport',
      desc: 'Services de transport fiables pour équipements, matériaux et enlèvement de débris. Desservant Montréal et les environs.',
      fleet: 'Flotte de transport',
      fleetDesc: 'Camions et remorques pour tous vos besoins de transport',
      scheduleTransport: 'Planifier un transport',
      services: 'Services de transport',
      serviceItems: [
        { title: 'Transport d\'équipement', desc: 'Transport sûr et sécurisé de machinerie lourde et d\'équipement de construction.' },
        { title: 'Livraison de matériaux', desc: 'Livraison ponctuelle de matériaux de construction sur votre chantier.' },
        { title: 'Enlèvement de débris', desc: 'Enlèvement et élimination efficaces des débris et déchets de construction.' },
        { title: 'Transport spécialisé', desc: 'Solutions de transport personnalisées pour charges uniques ou surdimensionnées.' }
      ],
      serviceAreasTitle: 'Zones de service',
      serviceAreasDesc: 'Nous offrons des services de transport dans Lanaudière, Laurentides, Laval, Montréal et les environs.',
      areas: ['Lanaudière', 'Laurentides', 'Laval', 'Montréal', 'Rive-Sud'],
      requestTransport: 'Demander un transport'
    },
    footer: {
      desc: 'Services de construction de qualité pour projets résidentiels, commerciaux et industriels à travers Lanaudière, Laurentides, Laval et Montréal.',
      quickLinks: 'Liens rapides',
      ourDivisions: 'Nos divisions',
      contactUs: 'Nous joindre',
      rights: 'Tous droits réservés.'
    },
    servicesList: {
      kitchen: {
        title: 'Rénovations de cuisine',
        desc: 'Transformez votre cuisine en un espace moderne et fonctionnel. De l\'installation d\'armoires aux rénovations complètes.',
        features: ['Armoires sur mesure', 'Installation de comptoirs', 'Mise à niveau de plomberie', 'Travaux électriques', 'Planchers']
      },
      bathroom: {
        title: 'Rénovations de salle de bain',
        desc: 'Créez votre sanctuaire de salle de bain parfait avec nos services de rénovation experts.',
        features: ['Installation de carrelage', 'Remplacement de fixtures', 'Installation de vanité', 'Mise à niveau douche/bain', 'Modifications d\'accessibilité']
      },
      homeExtensions: {
        title: 'Extensions de maison et garages détachés',
        desc: 'Agrandissez votre espace de vie avec des ajouts personnalisés et la construction de garages indépendants.',
        features: ['Ajouts de pièces', 'Garages détachés', 'Suites pour beaux-parents', 'Vérandas', 'Ajouts de deuxième étage']
      },
      industrial: {
        title: 'Construction industrielle',
        desc: 'Construction industrielle lourde et améliorations d\'installations.',
        features: ['Mises à niveau d\'installations', 'Fondations d\'équipements', 'Améliorations de sécurité', 'Quais de chargement', 'Planchers industriels']
      },
      commercial: {
        title: 'Construction commerciale',
        desc: 'Services de construction commerciale professionnels pour entreprises de toutes tailles.',
        features: ['Améliorations locatives', 'Aménagements de bureaux', 'Espaces de vente au détail', 'Construction de restaurants', 'Modifications d\'entrepôts']
      },
      maintenance: {
        title: 'Entretien de propriété',
        desc: 'Services d\'entretien fiables pour garder votre propriété en parfait état.',
        features: ['Réparations et correctifs', 'Entretien saisonnier', 'Soins préventifs', 'Services d\'urgence', 'Tâches de bricolage']
      }
    }
  }
};

export default translations;
